import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import * as authApi from '../services/authApi';
import { extractErrorMessage } from '../../../shared/utils/errorHandling';

export default function AcceptInvite() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();
    const [state, setState] = useState({
        loading: true,
        error: null,
        invite: null,
        submitting: false,
        success: false
    });
    const [formData, setFormData] = useState({ password: '', confirmPassword: '' });

    useEffect(() => {
        if (!token) {
            setState((s) => ({ ...s, loading: false, error: 'Invalid invite link. No token provided.' }));
            return;
        }
        authApi.verifyInvite(token)
            .then((res) => {
                setState((s) => ({ ...s, loading: false, invite: res.data, error: null }));
            })
            .catch((err) => {
                const msg = err.response?.data?.message || extractErrorMessage(err) || 'This invite link is invalid or has expired.';
                setState((s) => ({ ...s, loading: false, error: msg }));
            });
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!token || state.submitting) {
            return;
        }
        const { password, confirmPassword } = formData;
        if (!password || password.length < 6) {
            setState((s) => ({ ...s, error: 'Password must be at least 6 characters' }));
            return;
        }
        if (password !== confirmPassword) {
            setState((s) => ({ ...s, error: 'Passwords do not match' }));
            return;
        }
        setState((s) => ({ ...s, error: null, submitting: true }));
        try {
            await authApi.acceptInvite(token, password);
            setState((s) => ({ ...s, submitting: false, success: true }));
            setTimeout(() => navigate('/login'), 2500);
        } catch (err) {
            const msg = err.response?.data?.message || extractErrorMessage(err) || 'Failed to create account.';
            setState((s) => ({ ...s, submitting: false, error: msg }));
        }
    };

    if (state.loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#0D0221] p-4">
                <div className="text-white text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00FFD0] mx-auto mb-4" />
                    <p>Verifying your invite...</p>
                </div>
            </div>
        );
    }

    if (state.error && !state.invite) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#0D0221] p-4">
                <div className="max-w-md w-full bg-[#14052e] rounded-xl p-8 border border-white/10 text-center">
                    <h1 className="text-xl font-bold text-white mb-4">Invalid Invite</h1>
                    <p className="text-gray-300 mb-6">{state.error}</p>
                    <button
                        onClick={() => navigate('/login')}
                        className="px-6 py-2 bg-[#00FFD0] text-[#0D0221] font-semibold rounded-lg hover:opacity-90"
                    >
                        Go to Login
                    </button>
                </div>
            </div>
        );
    }

    if (state.success) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#0D0221] p-4">
                <div className="max-w-md w-full bg-[#14052e] rounded-xl p-8 border border-white/10 text-center">
                    <h1 className="text-xl font-bold text-[#00FFD0] mb-4">Account Created!</h1>
                    <p className="text-gray-300 mb-4">
                        You can now sign in with your email and password. Redirecting to login...
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#0D0221] p-4">
            <div className="max-w-md w-full bg-[#14052e] rounded-xl p-8 border border-white/10">
                <h1 className="text-xl font-bold text-white mb-2">Join {state.invite?.merchantName || 'the team'}</h1>
                <p className="text-gray-400 text-sm mb-6">
                    Create your password to accept the invite as {state.invite?.email}
                </p>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm text-gray-400 mb-1">Email</label>
                        <input
                            type="email"
                            value={state.invite?.email || ''}
                            readOnly
                            className="w-full px-4 py-3 bg-[#0D0221] border border-white/20 rounded-lg text-white opacity-80"
                        />
                    </div>
                    <div>
                        <label className="block text-sm text-gray-400 mb-1">Password</label>
                        <input
                            type="password"
                            value={formData.password}
                            onChange={(e) => setFormData((f) => ({ ...f, password: e.target.value }))}
                            placeholder="At least 6 characters"
                            className="w-full px-4 py-3 bg-[#0D0221] border border-white/20 rounded-lg text-white placeholder-gray-500 focus:border-[#00FFD0] focus:outline-none"
                        />
                    </div>
                    <div>
                        <label className="block text-sm text-gray-400 mb-1">Confirm Password</label>
                        <input
                            type="password"
                            value={formData.confirmPassword}
                            onChange={(e) => setFormData((f) => ({ ...f, confirmPassword: e.target.value }))}
                            placeholder="Repeat password"
                            className="w-full px-4 py-3 bg-[#0D0221] border border-white/20 rounded-lg text-white placeholder-gray-500 focus:border-[#00FFD0] focus:outline-none"
                        />
                    </div>
                    {state.error && (
                        <div className="p-3 bg-red-500/20 text-red-400 rounded-lg text-sm">{state.error}</div>
                    )}
                    <button
                        type="submit"
                        disabled={state.submitting}
                        className="w-full py-3 bg-gradient-to-r from-[#00FFD0] to-[#FF4DA6] text-[#0D0221] font-bold rounded-lg hover:opacity-90 disabled:opacity-50"
                    >
                        {state.submitting ? 'Creating account...' : 'Create Account'}
                    </button>
                </form>
                <p className="text-center text-gray-500 text-sm mt-4">
                    Already have an account?{' '}
                    <button type="button" onClick={() => navigate('/login')} className="text-[#00FFD0] hover:underline">
                        Sign in
                    </button>
                </p>
            </div>
        </div>
    );
}
